import {
  isValueEmpty,
  nonInitials,
  validatePassword,
  validatePhone,
} from '@taleemabad/dsm/components';
import {
  DocumentScanner,
  ScanDocumentOptions,
} from '@therealabdi27/capacitor-document-scanner-v2';
import { Capacitor } from '@capacitor/core';
import {
  OutputFormats,
  TaleemabadCapacitorPlugin,
} from 'taleemabad-capacitor-plugin';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { SupportedGradeSubjects } from '@taleemabad/db';
import { Platform } from '@taleemabad/shared';
export const validatePhoneNumber = (value: string) => {
  if (!nonInitials(value)) {
    return 'Phone number must start with 3 or 0.';
  } else if (isValueEmpty(value)) {
    return 'Please enter your phone number.';
  } else if (!validatePhone(value)) {
    return 'Please enter a valid phone number.';
  } else {
    return '';
  }
};

// Constant for minimum international phone number length
const MIN_INTERNATIONAL_PHONE_LENGTH = 8;

// International phone validation (used by signup form with international=true)
export const validateInternationalPhoneNumber = (value: string) => {
  // Remove any formatting characters (spaces, dashes, parentheses) for validation
  const normalizedValue = value.replace(/[\s\-()]/g, '');

  if (isValueEmpty(normalizedValue)) {
    return 'Please enter your phone number.';
  }

  // For international phone numbers, validate minimum length (country code + number)
  // Minimum valid international number is around 7-8 digits
  if (normalizedValue.length < MIN_INTERNATIONAL_PHONE_LENGTH) {
    return 'Please enter a valid phone number';
  }

  return '';
};

export const validatePasswords = (value: string) => {
  if (isValueEmpty(value)) {
    return 'Please enter your password';
  } else if (!validatePassword(value)) {
    return 'Password must be at least 4 characters.';
  } else {
    return '';
  }
};

export const validateSignupPassword = (value: string): string | undefined => {
  if (!value) return 'Password is required';
  if (value.length < 5) return 'At least 5 characters';
  if (!(/[a-zA-Z]/.test(value) && /\d/.test(value)))
    return 'Include letter + number';
  return undefined;
};

export const validateSignupConfirmPassword = (
  confirmPassword: string,
  password: string,
): string | undefined => {
  if (!confirmPassword) return 'Confirm password';
  if (confirmPassword !== password) return 'Passwords do not match';
  return undefined;
};

export const scanDocument = async (options?: ScanDocumentOptions) => {
  const { scannedImages } = await DocumentScanner.scanDocument(options);
  if (scannedImages && scannedImages.length > 0) {
    return scannedImages.map((image: string) => ({
      localUrl: Capacitor.convertFileSrc(image),
    }));
  }
  return [];
};

interface StartScanOptions {
  pageLimit?: number;
  mode?: string;
  enableGalleryImport?: boolean;
  outputFormats?: OutputFormats;
}

interface CapturedImage {
  localUrl: string;
  isBlurry: boolean;
}

export const captureDocument = async (
  options?: StartScanOptions,
): Promise<CapturedImage[]> => {
  try {
    const platform = Capacitor.getPlatform();
    let images: string[] = [];
    // google mlkit scanner is not available on ios, hence we need to use the document scanner plugin
    if (platform === Platform.Ios) {
      const { scannedImages } = await DocumentScanner.scanDocument();
      if (scannedImages && scannedImages.length > 0) {
        images = scannedImages;
      }
    } else {
      const scannedResult = await TaleemabadCapacitorPlugin.startScan(options);
      if (scannedResult.images && scannedResult.images.length > 0) {
        images = scannedResult.images;
      }
    }

    if (images.length > 0) {
      // mlkit stores images in cache folder by default, we don't want that since the images will be deleted if low storage threshold is reached.
      // So to avoid that we copy the images to persistent storage
      const persistentImages = await storeImagesInPersistentStorage(images);

      const processedImages: CapturedImage[] = [];

      for (const uri of persistentImages) {
        let blurry = false;
        blurry = await isImageBlurry(uri);
        processedImages.push({
          localUrl: Capacitor.convertFileSrc(uri),
          isBlurry: blurry,
        });
      }
      return processedImages;
    }
  } catch (error) {
    console.error('Error capturing document:', error);
  }
  return [];
};

export const storeImagesInPersistentStorage = async (
  imagePaths: string[],
): Promise<string[]> => {
  const persistentImages: string[] = [];

  for (let i = 0; i < imagePaths.length; i++) {
    const imagePath = imagePaths[i];
    try {
      const fileData = await Filesystem.readFile({
        path: imagePath,
      });

      const newFileName = `docscan_${Date.now()}.jpg`;

      await Filesystem.writeFile({
        path: newFileName,
        data: fileData.data,
        directory: Directory.Data,
      });

      const { uri } = await Filesystem.getUri({
        directory: Directory.Data,
        path: newFileName,
      });

      persistentImages.push(uri);
    } catch (err) {
      console.error('Error while copying file to persistent storage:', err);
    }
  }

  return persistentImages;
};

/**
 * detects if an image is blurry to improve the accuracy of AI-driven processes,
 * such as automated exam paper checking. Blurry images can significantly degrade
 * the performance of these systems. This function implements a client-side blur
 * detection mechanism using the variance of the Laplacian method.
 *
 * the core idea is that sharp images exhibit more edge detail (high-frequency content),
 * resulting in a higher variance of the Laplacian response. Conversely, blurred images
 * have smoother regions and thus a lower variance.
 *
 * by calculating this variance and comparing it against a calibrated threshold (e.g.,
 * a variance below 15 often indicates a blurry image based on analysis of past
 * submissions), we can identify potentially problematic images. If an image is
 * flagged as blurry, the user can be prompted to retake it, ensuring higher quality
 * inputs for subsequent AI processing and reducing wasted attempts on poor-quality images.
 *
 * @param imageUri - URI of the image to be processed.
 * @param threshold - variance threshold below which the image is considered blurry.
 *                    Based on findings, a threshold around 10 is effective for identifying
 *                    definitively blurry images.
 * @returns resolves to true if the image is blurry (variance < threshold),
 *          false otherwise.
 */
export const isImageBlurry = async (
  imageUri: string,
  threshold = 15,
): Promise<boolean> => {
  return new Promise<boolean>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      // using an off-screen canvas to get pixel data without affecting the UI.
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return reject(new Error('Could not get canvas context'));
      }
      ctx.drawImage(image, 0, 0);

      const { width, height } = canvas;
      const imageData = ctx.getImageData(0, 0, width, height);
      const pixels = imageData.data;
      const gray = new Uint8Array(width * height);

      // convert image to grayscale because edge detection is typically performed on intensity values.
      // the luminance formula (0.299R + 0.587G + 0.114B) is used for conversion.
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        gray[i / 4] = 0.299 * r + 0.587 * g + 0.114 * b;
      }

      // apply a 3x3 Laplacian filter to detect edges.
      let sum = 0;
      let sumSq = 0;
      const w = width;
      const h = height;
      // process only inner pixels to avoid issues with boundary conditions when applying the filter.
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const index = y * w + x;
          const lap =
            4 * gray[index] -
            gray[index - w] - // pixel above
            gray[index + w] - // pixel below
            gray[index - 1] - // pixel to the left
            gray[index + 1]; // pixel to the right

          sum += lap;
          sumSq += lap * lap;
        }
      }
      const numPixels = (width - 2) * (height - 2);
      const mean = sum / numPixels;
      const variance = sumSq / numPixels - mean * mean;

      // a low variance suggests fewer sharp edges, indicating the image might be blurry.
      resolve(variance < threshold);
    };

    image.onerror = (error) => {
      reject(new Error('Image load error: ' + error));
    };

    image.src =
      imageUri.startsWith('blob:') || imageUri.startsWith('http')
        ? imageUri
        : Capacitor.convertFileSrc(imageUri);
  });
};

export const formatLocalTime = (dateStr?: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  const datePart = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  if (!dateStr.includes('T') && !dateStr.includes(':')) {
    return datePart;
  }

  return (
    datePart +
    ' | ' +
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  );
};

export const getPageLimitBasedOnMemory = (): number => {
  if ('deviceMemory' in navigator) {
    const memory = navigator.deviceMemory as number;
    if (memory <= 4) {
      return 10;
    }
  } else {
    console.info('The device memory information is not available.');
  }
  return 50;
};

export const toPascal = (str: string): string => {
  return str
    .replace(/[-_]+/g, ' ')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) => {
      return index === 0 ? match.toUpperCase() : match.toLowerCase();
    })
    .replace(/\s+/g, '');
};

export const validateNameInput = (value: string) => {
  return !/^[a-zA-Z\s.-]*$/.test(value);
};

export const capitalizeFirstLetter = (string: string) => {
  return string?.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const removeSpecificHistory = (routeName: string) => {
  window.history.pushState(routeName, '', window.location.pathname);

  // this function is  used to set the history state to null on specific routes to avoid back button navigation
  for (let i = window.history.length - 1; i >= 0; i--) {
    const historyState = window.history.state;
    if (historyState === routeName) {
      window.history.pushState(null, '', '');
      break;
    }
  }
};
export const updateTheme = (theme: string) => {
  import(`../../../../libs/dsm/themes/src/themes/${theme}.scss`).catch(
    (err) => {
      console.error('Error updating theme:', err);
    },
  );
};

/** Checks if assigned classes include any supported grade subjects from the predefined list */
export const hasAssignedSupportedGradeSubjects = (
  assignedClasses: Array<{ gradeSubject?: string | number }> | null,
): boolean => {
  return (
    assignedClasses?.some(
      (entry) =>
        entry?.gradeSubject &&
        SupportedGradeSubjects.includes(Number(entry.gradeSubject)),
    ) || false
  );
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const formatLocalISODate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const GRADE_MAP: Record<string, string> = {
  '1': 'One',
  '2': 'Two',
  '3': 'Three',
  '4': 'Four',
  '5': 'Five',
  '6': 'Six',
  '7': 'Seven',
  '8': 'Eight',
};

export const SUBJECT_MAP: Record<string, string> = {
  eng: 'English',
  math: 'Math',
  mat: 'Math',
  urd: 'Urdu',
  sci: 'Science',
  gk: 'General Knowledge',
  isl: 'Islamiat',
  sst: 'Social Studies',
};

export const normalizeLabel = (
  label: string | null | undefined,
  type: 'grade' | 'subject',
): string => {
  if (!label) return typeof label === 'string' ? label : '';
  const clean = label.trim().toLowerCase();

  if (type === 'grade') {
    // Standardize "Grade 2" or "2" to "Grade Two"
    const digit = clean.replace('grade', '').trim();
    if (GRADE_MAP[digit]) return `Grade ${GRADE_MAP[digit]}`;
    // If it's already "Grade Two", Ensure proper title casing
    return label
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  if (type === 'subject') {
    if (SUBJECT_MAP[clean]) return SUBJECT_MAP[clean];
    return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
  }

  return label;
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove data:image/jpeg;base64, prefix for Filesystem.writeFile
      resolve(result.split(',')[1] || result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};
